/**
 * @module api/customerApi
 * @description customer-back(대고객 백엔드) 전용 Axios 인스턴스 및 호출 래퍼.
 *   - SURINP-342 D3: 관리 사이트가 customer-back 의 `/admin/**` 를 직접 호출한다.
 *   - cs-back 용 `/api/cs` 인스턴스를 재사용하지 않는다 — baseURL 이 다르고, 그쪽 인터셉터는
 *     쓰기 요청 전에 cs-back 의 `/auth/token/refresh` 를 호출하기 때문이다(없는 경로가 된다).
 *   - 인증은 관리 사이트가 이미 갖고 있는 cs-back JWT 를 그대로 보낸다. customer-back 이
 *     같은 시크릿으로 검증한 뒤 `users` 를 다시 조회해 등급을 판정한다.
 */
import axios from 'axios'
import store from '@/store'
import { getErrorStatus, getErrorData, handleSessionUnauthorized } from '@/api/interceptors'

/**
 * customer-back 전용 인스턴스.
 * 기본값은 devServer / 리버스 프록시 경유 = 동일 오리진이므로 CORS 가 필요 없다.
 * context-path(`/app/v1`)는 baseURL 이 갖는다. `VUE_APP_CUSTOMER_API_BASE` 를 채우면
 * 교차 오리진이 되어 customer-back 의 CORS 허용 오리진 설정이 선행 조건이 된다(기본은 빈 값).
 */
const customerAxios = axios.create({
  baseURL: process.env.VUE_APP_CUSTOMER_API_BASE || '/api/customer/app/v1',
  // api.js 가 `axios.defaults.withCredentials = true` 를 전역 설정한다.
  // customer-back 은 Bearer 토큰만 읽고 쿠키를 쓰지 않으므로 인스턴스 단위로 끈다.
  withCredentials: false
})

// --- 요청 인터셉터: 인증 토큰 주입 ---
// cs-front 관례대로 `Bearer` 접두어 없이 원문을 넣는다(interceptors.js 와 동일).
customerAxios.interceptors.request.use(
  (config) => {
    const authToken = store.state.auth.user?.id?.authToken
    if (authToken) {
      config.headers.Authorization = authToken
    }
    return config
  },
  (error) => Promise.reject(error)
)

// --- 응답 인터셉터: **401 만** 기존 세션 만료 경로 재사용 ---
// setupInterceptors() 는 붙이지 않는다 — 그 안의 자동 토큰 갱신이 cs-back 을 호출한다.
// 400/409 는 손대지 않고 호출자에게 상태코드째 넘긴다(noCustomApi 패턴).
// `_skipSessionHandler` 가 붙은 요청은 실패를 삼켜야 하는 호출(대기 건수 배지)이므로 건너뛴다.
//
// ⚠️ **403 을 여기서 넘기지 않는다**(2026-08-19 통합 판단). handleSessionUnauthorized 는
//    401·403 을 함께 잡아 store.commit('user', {}) + authToken 제거 + /login 리다이렉트를 한다.
//    customer-back 의 403 은 "세션이 죽었다" 가 아니라 **"이 등급은 승인 권한이 없다"** 다
//    (ADMIN_READ 는 있고 ADMIN_WRITE 가 없는 계정 — AdminSecurityConfig). 그것을 세션 만료로
//    처리하면 승인 버튼 한 번에 **다른 탭의 cs-back 작업까지 전부 로그아웃**된다.
//    등급 미달 계정은 401 로 오므로(AdminAuthenticationFilter) 세션 경로는 그쪽이 덮는다.
const CUSTOMER_SESSION_EXPIRED_STATUS = 401

customerAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    if (
      !error.config?._skipSessionHandler &&
      status === CUSTOMER_SESSION_EXPIRED_STATUS
    ) {
      handleSessionUnauthorized(error)
    }
    return Promise.reject(error)
  }
)

/**
 * customer-back 을 호출하고 상태코드와 바디를 그대로 돌려줍니다.
 * 성공/실패 모두 `{ status, data }` 형태이므로 호출자가 409(이미 처리됨) 등을 직접 분기합니다.
 *
 * @param {string} url - baseURL 이후의 경로 (예: `/admin/partnership-inquiries`)
 * @param {string} method - HTTP 메서드 (get, post)
 * @param {Object} [data] - 요청 바디
 * @param {Object} [config] - 추가 Axios 설정 (`_skipSessionHandler` 등)
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const customerApi = async (url, method, data, config = {}) => {
  try {
    const response = await customerAxios({ url, method, data, ...config })
    return { status: response.status, data: response.data }
  } catch (e) {
    return { status: getErrorStatus(e), data: getErrorData(e) }
  }
}

/**
 * 인증된 GET 요청을 수행합니다. 401 만 세션 만료 경로를 탑니다(403 = 등급 미달은 호출자가 분기).
 *
 * @param {string} url - 요청 URL
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const customerGetApi = async (url) => customerApi(url, 'get')

/**
 * 인증된 POST 요청을 수행합니다. 401 만 세션 만료 경로를 탑니다(403 = 등급 미달은 호출자가 분기).
 *
 * @param {string} url - 요청 URL
 * @param {Object} [data] - 요청 바디
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const customerPostApi = async (url, data) => customerApi(url, 'post', data)

/**
 * 실패를 삼켜야 하는 GET 요청을 수행합니다(대기 건수 배지).
 * 401/403 이어도 세션을 만료시키지 않습니다 — 배지가 안 뜰 뿐 메뉴가 죽으면 안 된다.
 * customer-back 의 관리자 시크릿이 아직 주입되지 않으면 `/admin/**` 가 전건 401 이므로,
 * 이 호출이 세션 만료 처리를 타면 로그인 직후 곧바로 /login 으로 튕긴다.
 *
 * @param {string} url - 요청 URL
 * @returns {Promise<{status: number|null, data: any}>}
 */
export const customerSilentGetApi = async (url) =>
  customerApi(url, 'get', undefined, { _skipSessionHandler: true })
